<template>
  <div class="w-full lg:grid lg:grid-cols-1 min-h-[100vh] pt-14 sm:pt-0">
    <div class="flex items-center justify-center py-12 sm:pt-[150px]">
      <div class="mx-auto grid w-full p-4 sm:p-0 sm:w-[450px] gap-10">
        <div class="grid gap-2 text-center">
          <div class="flex flex-col items-center gap-4 mx-auto mb-2">
            <img src="https://dashboard.hoopay.com.br/img/hoopay-logo.png" alt="Logo Hoopay"
              class="max-h-[50px] w-auto object-contain" />
            <h1 class="text-3xl font-bold">
              Verificação em Duas Etapas
            </h1>
          </div>
          <p class="text-muted-foreground">
            Digite o código de 6 dígitos gerado pelo seu aplicativo de autenticação.
          </p>
        </div>

        <div class="grid gap-6">
          <!-- Inputs de código (6 dígitos em quadrados) -->
          <div class="flex justify-center gap-3">
            <input v-for="(digit, index) in 6" :key="index"
              :ref="(el) => { digitInputs[index] = el as HTMLInputElement | null }" v-model="codeArray[index]"
              type="text" maxlength="1" inputmode="numeric" pattern="[0-9]*" @input="handleCodeInput(index, $event)"
              @keydown="handleKeyDown(index, $event)" @paste="handlePaste" @focus="handleFocus(index)"
              :disabled="isAuthLoading"
              class="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-background border-2 border-input rounded-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" />
          </div>

          <div class="grid gap-4 mt-4">
            <Button type="submit" class="w-full py-6" :disabled="isAuthLoading || codeArray.join('').length !== 6"
              @click="handleValidate">
              <Loader2 v-if="isAuthLoading" class="w-4 h-4 mr-2 animate-spin" />
              Verificar Código
            </Button>
            <Button variant="outline" class="w-full py-6" @click="$router.push({ name: 'login' })">
              Voltar para o Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toaster />
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast/use-toast'
import { Toaster } from '@/components/ui/toast'
import { ref, reactive, onMounted } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'
import { useTenant } from '@/composables/useTenant'

const { toast } = useToast()
const { validate2AF, isAuthLoading } = useAuth()
const { isTradersPlatform } = useTenant()
const router = useRouter()

// Refs para os inputs individuais
const digitInputs = ref<(HTMLInputElement | null)[]>([])
const codeArray = reactive<string[]>(Array(6).fill(''))

const handleCodeInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  // Permitir apenas números
  if (!/^\d?$/.test(value)) {
    input.value = ''
    codeArray[index] = ''
    return
  }

  codeArray[index] = value

  // Mover para o próximo input se digitou um número
  if (value && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }
}

const handleKeyDown = (index: number, event: KeyboardEvent) => {
  const key = event.key

  // Se pressionar backspace e o campo estiver vazio, voltar para o anterior
  if (key === 'Backspace' && !codeArray[index] && index > 0) {
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se as setas forem pressionadas
  if (key === 'ArrowLeft' && index > 0) {
    digitInputs.value[index - 1]?.focus()
  }
  if (key === 'ArrowRight' && index < 5) {
    digitInputs.value[index + 1]?.focus()
  }

  // Se pressionar Enter e o código estiver completo
  if (key === 'Enter' && codeArray.join('').length === 6) {
    handleValidate()
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const pastedData = event.clipboardData?.getData('text') || ''
  const numbers = pastedData.replace(/\D/g, '').slice(0, 6)

  // Preencher os campos com os números colados
  for (let i = 0; i < 6; i++) {
    codeArray[i] = numbers[i] || ''
  }

  // Focar no último campo preenchido ou no último campo
  const lastFilledIndex = Math.min(numbers.length, 5)
  setTimeout(() => {
    digitInputs.value[lastFilledIndex]?.focus()
  }, 10)
}

const handleFocus = (index: number) => {
  setTimeout(() => {
    digitInputs.value[index]?.select()
  }, 10)
}

const handleValidate = () => {
  const fullCode = codeArray.join('')
  if (fullCode.length !== 6) return

  validate2AF(
    fullCode,
    () => {
      toast({
        title: 'Sucesso',
        description: 'Autenticação realizada com sucesso!',
      })

      if (isTradersPlatform) {
        router.push({ name: 'trader.home' })
      } else {
        router.push({ name: 'courses.my' })
      }
    },
    () => {
      toast({
        title: 'Erro na validação',
        description: 'Código 2FA inválido ou expirado.',
        variant: 'destructive'
      })
      // Limpa o código em caso de erro para tentar novamente
      for (let i = 0; i < 6; i++) codeArray[i] = ''
      digitInputs.value[0]?.focus()
    }
  )
}

onMounted(() => {
  // Auto-focus no primeiro input ao carregar
  setTimeout(() => {
    digitInputs.value[0]?.focus()
  }, 100)
})
</script>

<style scoped>
/* Remove setas de input number (caso algum navegador interprete assim) */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
